'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, useEffect, useRef } from 'react';

import { TooltipProvider } from '@hr/ui';
import { useAuthStore, type UserProfile, type TenantContext } from '@/lib/stores/auth-store';
import { getAccessToken, isTokenExpired, clearTokens } from '@/lib/auth';
import { api } from '@/lib/api';

/**
 * AuthInitializer - Restores auth state on app load
 *
 * On mount, checks for a valid access token in localStorage.
 * If found, fetches user profile from /auth/me to restore the full auth state.
 */
function AuthInitializer() {
  const setAuth = useAuthStore((state) => state.setAuth);
  const setLoading = useAuthStore((state) => state.setLoading);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Only run once
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    async function initializeAuth() {
      const token = getAccessToken();

      // No token - not authenticated
      if (!token) {
        setLoading(false);
        return;
      }

      // Token expired - clear and redirect to login
      if (isTokenExpired(token)) {
        clearTokens();
        setLoading(false);
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login?expired=true';
        }
        return;
      }

      // Token exists and is valid - fetch user profile to restore auth state
      try {
        const response = await api.auth.me() as {
          user: {
            id: string;
            email: string;
            mfaEnabled: boolean;
            emailVerified: boolean;
            isPlatformAdmin?: boolean;
          };
          membership: {
            id: string;
            role: string;
            tenantId: string;
            tenantName: string;
            tenantSlug: string;
            employeeId: string | null;
            onboardingCompleted: boolean;
          } | null;
          tenants: Array<{
            id: string;
            name: string;
            slug: string;
            role: string;
          }>;
        };

        // Build user profile
        const user: UserProfile = {
          id: response.user.id,
          email: response.user.email,
          mfaEnabled: response.user.mfaEnabled,
          emailVerified: response.user.emailVerified,
          isPlatformAdmin: response.user.isPlatformAdmin ?? false,
        };

        // Build tenant contexts from tenants array
        const availableTenants: TenantContext[] = (response.tenants || []).map((tenant) => ({
          id: tenant.id,
          slug: tenant.slug,
          name: tenant.name,
          role: tenant.role as TenantContext['role'],
          employeeId: null,
        }));

        // Use current membership tenant as current tenant
        if (response.membership) {
          const onboardingCompleted = response.membership.onboardingCompleted ?? true;

          const currentTenant: TenantContext = {
            id: response.membership.tenantId,
            slug: response.membership.tenantSlug,
            name: response.membership.tenantName,
            role: response.membership.role as TenantContext['role'],
            employeeId: response.membership.employeeId ?? null,
            onboardingCompleted,
          };

          // Set auth state
          setAuth({
            accessToken: token,
            user,
            tenant: currentTenant,
            availableTenants,
          });

          // Set Sentry user context
          import(/* webpackIgnore: true */ '@sentry/nextjs')
            .then((Sentry) => Sentry.setUser({ id: user.id, email: user.email }))
            .catch(() => {});

          // Redirect to setup wizard if onboarding is not complete
          if (!onboardingCompleted && typeof window !== 'undefined') {
            const currentPath = window.location.pathname;
            if (currentPath !== '/setup') {
              window.location.href = '/setup';
            }
          }
        } else {
          // User has no tenant membership - clear auth
          clearTokens();
          import(/* webpackIgnore: true */ '@sentry/nextjs').then((Sentry) => Sentry.setUser(null)).catch(() => {});
          setLoading(false);
        }
      } catch (error) {
        // API call failed - token might be invalid
        console.error('Failed to restore auth state:', error);
        clearTokens();
        import(/* webpackIgnore: true */ '@sentry/nextjs').then((Sentry) => Sentry.setUser(null)).catch(() => {});
        setLoading(false);
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login?expired=true';
        }
      }
    }

    // Only initialize if not already authenticated (e.g., just logged in)
    if (!isAuthenticated) {
      initializeAuth();
    } else {
      setLoading(false);
    }
  }, [setAuth, setLoading, isAuthenticated]);

  return null;
}

export function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000, // 1 minute
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthInitializer />
        {children}
      </TooltipProvider>
    </QueryClientProvider>
  );
}
